import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Phone, UserCheck, AlertCircle, X, Check, Clock, Users, UserMinus, ShoppingCart, ThumbsUp, ThumbsDown, Minus, ArrowRight, Search, Filter, SortAsc, SortDesc, ChevronLeft, Menu, Flame, EyeOff } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { authenticatedFetch } from '../utils/apiUtils';
import UserProfile from './UserProfile';
import UserPanel from './UserPanel';
import CallRecording from './CallRecording';

const CRM = () => {
  // Stati per il workflow
  const [workflowLists, setWorkflowLists] = useState({
    jkVerkauft: [],
    jkAftersales: [],
    autoSchiessVerkauft: [],
    autoSchiessAftersales: [],
    fruhlingCheck: []
  });
  const [selectedList, setSelectedList] = useState(null); // Lista attualmente selezionata (inizia come null)
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [offerInterest, setOfferInterest] = useState(null);
  const [offerInterestSaving, setOfferInterestSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Flag per tracciare l'inizializzazione
  
  // Determina la lista iniziale basata sul dipartimento dell'utente
  const getInitialSelectedList = () => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const userDepartment = currentUser?.department;
    const isAdmin = currentUser?.role === 'admin';
    
    if (isAdmin || !userDepartment || userDepartment === '' || userDepartment === null) {
      return 'jkVerkauft'; // Default per admin o utenti senza dipartimento
    } else if (userDepartment === 'JK') {
      return 'jkVerkauft';
    } else if (userDepartment === 'Auto Schiess' || userDepartment === 'AutoSchiess') {
      return 'autoSchiessVerkauft';
    } else if (userDepartment === 'Special') {
      return 'fruhlingCheck';
    }
    return 'jkVerkauft'; // Fallback
  };
  
  // Effect per impostare la lista iniziale corretta
  useEffect(() => {
    if (selectedList === null && !isInitialized) {
      const initialList = getInitialSelectedList();
      console.log('🎯 Impostando lista iniziale:', initialList);
      setSelectedList(initialList);
      setIsInitialized(true);
    }
  }, [selectedList, isInitialized]);
  const [workflowStats, setWorkflowStats] = useState({
    jkVerkauft: { total: 0, open: 0, sold: 0, closed: 0 },
    jkAftersales: { total: 0, open: 0, sold: 0, closed: 0 },
    autoSchiessVerkauft: { total: 0, open: 0, sold: 0, closed: 0 },
    autoSchiessAftersales: { total: 0, open: 0, sold: 0, closed: 0 },
    fruhlingCheck: { total: 0, open: 0, sold: 0, closed: 0 }
  });

  // Funzione per formattare le date dei contratti (solo data, senza ora)
  const formatContractDate = (dateString) => {
    if (!dateString) {
      return 'Data non disponibile';
    }
    
    try {
      const date = new Date(dateString);
      
      // Verifica se la data è valida
      if (isNaN(date.getTime())) {
        console.error('Data contratto non valida:', dateString);
        return 'Data non valida';
      }
      
      // Formato tedesco per le date dei contratti: dd.mm.yyyy
      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (error) {
      console.error('Errore nel formattare la data contratto:', error);
      return 'Errore data';
    }
  };


  // Funzione per ordinamento intelligente dei clienti
  const sortCustomersIntelligently = (customers) => {
    const now = new Date();
    
    return customers.sort((a, b) => {
      // Dati per cliente A
      const aCallCount = callCounts[a.id] || 0;
      const aLastCall = lastCallTimes[a.id];
      const aCreatedAt = new Date(a.created_at);
      
      // Dati per cliente B  
      const bCallCount = callCounts[b.id] || 0;
      const bLastCall = lastCallTimes[b.id];
      const bCreatedAt = new Date(b.created_at);
      
      // 0. PRIORITÀ SPECIALE: Clienti "closed" vanno sempre alla fine
      if (a.status === 'closed' && b.status !== 'closed') return 1;
      if (b.status === 'closed' && a.status !== 'closed') return -1;
      
      // Se entrambi sono "closed", ordina per data inserimento (più recenti prima)
      if (a.status === 'closed' && b.status === 'closed') {
        return bCreatedAt - aCreatedAt;
      }
      
      // 1. PRIORITÀ MASSIMA: Clienti mai chiamati (esclusi i closed)
      if (aCallCount === 0 && bCallCount > 0) return -1;
      if (bCallCount === 0 && aCallCount > 0) return 1;
      
      // 2. Se entrambi mai chiamati, ordina per data inserimento (più vecchi prima)
      if (aCallCount === 0 && bCallCount === 0) {
        return aCreatedAt - bCreatedAt;
      }
      
      // 3. Se entrambi chiamati, ordina per tempo ultima chiamata
      if (aLastCall && bLastCall) {
        const aLastCallTime = new Date(aLastCall);
        const bLastCallTime = new Date(bLastCall);
        
        // Più tempo passato dall'ultima chiamata = priorità maggiore
        const timeDiff = aLastCallTime - bLastCallTime;
        if (Math.abs(timeDiff) > 60000) { // Differenza > 1 minuto
          return timeDiff; // Chiamata più vecchia prima
        }
        
        // 4. A parità di tempo, meno tentativi = priorità maggiore
        const attemptDiff = aCallCount - bCallCount;
        if (attemptDiff !== 0) {
          return attemptDiff;
        }
      }
      
      // 5. Fallback: ordina per data inserimento
      return aCreatedAt - bCreatedAt;
    });
  };
  const [notes, setNotes] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [callLogs, setCallLogs] = useState([]);
  const [pendingFeedback, setPendingFeedback] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showUserPanel, setShowUserPanel] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [feedbackFilter, setFeedbackFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'smart', direction: 'ascending' });
  const [callCounts, setCallCounts] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [callPhase, setCallPhase] = useState('idle');
  const [callStartTime, setCallStartTime] = useState(null);
  const [callEndTime, setCallEndTime] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [lastPage, setLastPage] = useState(1); // Aggiungo questa variabile per memorizzare l'ultima pagina

  // Nuovi stati per il layout responsive
  const [activeView, setActiveView] = useState('campaigns'); // 'campaigns', 'customers', 'details'
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Modalità workflow sempre attiva per questo componente
  const [viewMode] = useState('workflow'); // Sempre workflow

  // Effetto per rilevare la dimensione dello schermo
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 1024);
    };

    // Imposta lo stato iniziale
    handleResize();

    // Aggiungi event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Funzione per tradurre gli stati in tedesco
  const translateStatus = (status) => {
    switch (status) {
      case 'open':
        return 'Neu';
      case 'sold':
        return 'Verkauft';
      case 'closed':
        return 'Geschlossen';
      case 'not_available':
        return 'Nicht erreicht';
      default:
        return status;
    }
  };

  // Funzione per effettuare una chiamata utilizzando il protocollo tel: standard
  const makeCall = (phoneNumber, customerId) => {
    // Rimuovi spazi e caratteri non numerici dal numero di telefono
    const formattedNumber = phoneNumber.replace(/\s+/g, '').replace(/[^\d+]/g, '');
    
    // Utilizziamo il protocollo tel: standard che è ampiamente supportato
    // Creiamo un elemento <a> e simuliamo un click per evitare problemi di navigazione
    const link = document.createElement('a');
    link.href = `tel:${formattedNumber}`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Rimuoviamo il link dopo il click
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    // Fallback: copia il numero negli appunti se l'API clipboard è disponibile
    try {
      if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(formattedNumber)
          .then(() => {
            console.log('Numero copiato negli appunti come backup');
          })
          .catch(err => {
            console.error('Errore nel copiare il numero negli appunti:', err);
          });
      } else {
        console.log('API Clipboard non disponibile in questo browser');
      }
    } catch (error) {
      console.error('Errore durante l\'accesso all\'API Clipboard:', error);
    }
  };

  useEffect(() => {
    if (selectedList) {
      fetchWorkflowCustomers(selectedList);
    }
  }, [selectedList, showClosed]); // showClosed come dipendenza triggera l'effetto quando cambia
  
  // useEffect iniziale per caricare le statistiche (una sola volta)
  useEffect(() => {
    // Carica le statistiche solo dopo che selectedList è stato impostato per la prima volta
    if (selectedList) {
      fetchAllWorkflowData();
    }
  }, []); // Torna alla dipendenza originale []

  useEffect(() => {
    if (selectedCustomer) {
      setSelectedFeedback(selectedCustomer.feedback);
      setCurrentStatus(selectedCustomer.status);
      fetchCallLogs(selectedCustomer.id);
      loadOfferInterest(selectedCustomer);
      setPendingFeedback(null);
      setPendingStatus(null);
      // Reset del workflow quando cambia il cliente selezionato
      setCallPhase('idle');
      setCallStartTime(null);
      setCallEndTime(null);
      setNotes('');
    } else {
      setSelectedFeedback(null);
      setCurrentStatus(null);
      setCallLogs([]);
      setOfferInterest(null);
      setPendingFeedback(null);
      setPendingStatus(null);
      // Reset del workflow quando non c'è nessun cliente selezionato
      setCallPhase('idle');
      setCallStartTime(null);
      setCallEndTime(null);
      setNotes('');
    }
  }, [selectedCustomer]);

  // Effetto per aggiornare le statistiche quando cambia showClosed
  useEffect(() => {
    if (selectedList) {
      fetchWorkflowCustomers(selectedList);
    }
  }, [showClosed]);
  
  // Effetto separato per ricaricare le statistiche quando cambia showClosed
  useEffect(() => {
    // Solo ricarica le statistiche, non i clienti della lista corrente
    const reloadStats = async () => {
      try {
        // Ottieni l'utente attuale dal localStorage
        const currentUser = JSON.parse(localStorage.getItem('user'));
        const isAdmin = currentUser?.role === 'admin';
        
        // Costruisci gli URL con il parametro show_closed solo se l'utente è admin e ha attivato il toggle
        const showClosedParam = (isAdmin && showClosed) ? '?show_closed=true' : '';
        
        // Fetch tutte le liste workflow in parallelo per calcolare solo le statistiche
        const [jkVerkauftRes, jkAftersalesRes, autoSchiessVerkauftRes, autoSchiessAftersalesRes, fruhlingCheckRes] = await Promise.all([
          authenticatedFetch(`${API_BASE_URL}/workflow/jk/verkauft/${showClosedParam}`),
          authenticatedFetch(`${API_BASE_URL}/workflow/jk/aftersales/${showClosedParam}`),
          authenticatedFetch(`${API_BASE_URL}/workflow/auto-schiess/verkauft/${showClosedParam}`),
          authenticatedFetch(`${API_BASE_URL}/workflow/auto-schiess/aftersales/${showClosedParam}`),
          authenticatedFetch(`${API_BASE_URL}/workflow/special/fruhling/`)
        ]);

        // Calcola le statistiche per ogni lista
        const newStats = {
          jkVerkauft: { total: 0, open: 0, sold: 0, closed: 0 },
          jkAftersales: { total: 0, open: 0, sold: 0, closed: 0 },
          autoSchiessVerkauft: { total: 0, open: 0, sold: 0, closed: 0 },
          autoSchiessAftersales: { total: 0, open: 0, sold: 0, closed: 0 },
          fruhlingCheck: { total: 0, open: 0, sold: 0, closed: 0 }
        };

        if (jkVerkauftRes.ok) {
          const data = await jkVerkauftRes.json();
          let customers = Array.isArray(data) ? data : [];
          
          // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
          if (!isAdmin || !showClosed) {
            customers = customers.filter(c => c.status !== 'closed');
          }
          
          newStats.jkVerkauft = {
            total: customers.length,
            open: customers.filter(c => c.status === 'open').length,
            sold: customers.filter(c => c.status === 'sold').length,
            closed: customers.filter(c => c.status === 'closed').length
          };
        }

        if (jkAftersalesRes.ok) {
          const data = await jkAftersalesRes.json();
          let customers = Array.isArray(data) ? data : [];
          
          // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
          if (!isAdmin || !showClosed) {
            customers = customers.filter(c => c.status !== 'closed');
          }
          
          newStats.jkAftersales = {
            total: customers.length,
            open: customers.filter(c => c.status === 'open').length,
            sold: customers.filter(c => c.status === 'sold').length,
            closed: customers.filter(c => c.status === 'closed').length
          };
        }

        if (autoSchiessVerkauftRes.ok) {
          const data = await autoSchiessVerkauftRes.json();
          let customers = Array.isArray(data) ? data : [];
          
          // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
          if (!isAdmin || !showClosed) {
            customers = customers.filter(c => c.status !== 'closed');
          }
          
          newStats.autoSchiessVerkauft = {
            total: customers.length,
            open: customers.filter(c => c.status === 'open').length,
            sold: customers.filter(c => c.status === 'sold').length,
            closed: customers.filter(c => c.status === 'closed').length
          };
        }

        if (autoSchiessAftersalesRes.ok) {
          const data = await autoSchiessAftersalesRes.json();
          let customers = Array.isArray(data) ? data : [];
          
          // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
          if (!isAdmin || !showClosed) {
            customers = customers.filter(c => c.status !== 'closed');
          }
          
          newStats.autoSchiessAftersales = {
            total: customers.length,
            open: customers.filter(c => c.status === 'open').length,
            sold: customers.filter(c => c.status === 'sold').length,
            closed: customers.filter(c => c.status === 'closed').length
          };
        }

        if (fruhlingCheckRes.ok) {
          const data = await fruhlingCheckRes.json();
          let customers = Array.isArray(data) ? data : [];
          if (!isAdmin || !showClosed) {
            customers = customers.filter(c => c.status !== 'closed');
          }
          newStats.fruhlingCheck = {
            total: customers.length,
            open: customers.filter(c => c.status === 'open').length,
            sold: customers.filter(c => c.status === 'sold').length,
            closed: customers.filter(c => c.status === 'closed').length
          };
        }

        setWorkflowStats(newStats);
      } catch (error) {
        console.error('Error reloading workflow stats:', error);
      }
    };
    
    reloadStats();
  }, [showClosed]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const fetchCallLogs = async (customerId) => {
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customerId}/calls/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCallLogs(data);
    } catch (error) {
      console.error('Error fetching call logs:', error);
      setCallLogs([]);
    }
  };

  const loadOfferInterest = async (customer) => {
    if (!customer || customer.current_department !== 'Special') {
      setOfferInterest(null);
      return;
    }
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/offer-interests/customer/${customer.id}`);
      if (!response.ok) {
        setOfferInterest(null);
        return;
      }
      const data = await response.json();
      setOfferInterest(data);
    } catch (error) {
      console.error('Error fetching offer interest:', error);
      setOfferInterest(null);
    }
  };

  const markOfferInterest = async () => {
    if (!selectedCustomer || offerInterest || offerInterestSaving) {
      return;
    }
    setOfferInterestSaving(true);
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/offer-interests/`, {
        method: 'POST',
        body: JSON.stringify({ customer_id: selectedCustomer.id })
      });
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        alert(errorBody.detail || 'Meldung fehlgeschlagen');
        return;
      }
      const data = await response.json();
      setOfferInterest(data);
    } catch (error) {
      console.error('Error marking offer interest:', error);
    } finally {
      setOfferInterestSaving(false);
    }
  };

  const fetchAllWorkflowData = async () => {
    try {
      // Ottieni l'utente attuale dal localStorage
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const isAdmin = currentUser?.role === 'admin';
      
      // Costruisci gli URL con il parametro show_closed solo se l'utente è admin e ha attivato il toggle
      const showClosedParam = (isAdmin && showClosed) ? '?show_closed=true' : '';
      
      // Fetch tutte le liste workflow in parallelo per calcolare le statistiche
      // Non usiamo showClosedParam perché gli endpoint workflow non lo supportano - filtriamo lato frontend
      const [jkVerkauftRes, jkAftersalesRes, autoSchiessVerkauftRes, autoSchiessAftersalesRes, fruhlingCheckRes] = await Promise.all([
        authenticatedFetch(`${API_BASE_URL}/workflow/jk/verkauft/`),
        authenticatedFetch(`${API_BASE_URL}/workflow/jk/aftersales/`),
        authenticatedFetch(`${API_BASE_URL}/workflow/auto-schiess/verkauft/`),
        authenticatedFetch(`${API_BASE_URL}/workflow/auto-schiess/aftersales/`),
        authenticatedFetch(`${API_BASE_URL}/workflow/special/fruhling/`)
      ]);

      // Calcola le statistiche per ogni lista
      const newStats = {
        jkVerkauft: { total: 0, open: 0, sold: 0, closed: 0 },
        jkAftersales: { total: 0, open: 0, sold: 0, closed: 0 },
        autoSchiessVerkauft: { total: 0, open: 0, sold: 0, closed: 0 },
        autoSchiessAftersales: { total: 0, open: 0, sold: 0, closed: 0 },
        fruhlingCheck: { total: 0, open: 0, sold: 0, closed: 0 }
      };

      if (jkVerkauftRes.ok) {
        const data = await jkVerkauftRes.json();
        let customers = Array.isArray(data) ? data : [];
        
        // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
        if (!isAdmin || !showClosed) {
          customers = customers.filter(c => c.status !== 'closed');
        }
        
        newStats.jkVerkauft = {
          total: customers.length,
          open: customers.filter(c => c.status === 'open').length,
          sold: customers.filter(c => c.status === 'sold').length,
          closed: customers.filter(c => c.status === 'closed').length
        };
      }

      if (jkAftersalesRes.ok) {
        const data = await jkAftersalesRes.json();
        let customers = Array.isArray(data) ? data : [];
        
        // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
        if (!isAdmin || !showClosed) {
          customers = customers.filter(c => c.status !== 'closed');
        }
        
        newStats.jkAftersales = {
          total: customers.length,
          open: customers.filter(c => c.status === 'open').length,
          sold: customers.filter(c => c.status === 'sold').length,
          closed: customers.filter(c => c.status === 'closed').length
        };
      }

      if (autoSchiessVerkauftRes.ok) {
        const data = await autoSchiessVerkauftRes.json();
        let customers = Array.isArray(data) ? data : [];
        
        // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
        if (!isAdmin || !showClosed) {
          customers = customers.filter(c => c.status !== 'closed');
        }
        
        newStats.autoSchiessVerkauft = {
          total: customers.length,
          open: customers.filter(c => c.status === 'open').length,
          sold: customers.filter(c => c.status === 'sold').length,
          closed: customers.filter(c => c.status === 'closed').length
        };
      }

      if (autoSchiessAftersalesRes.ok) {
        const data = await autoSchiessAftersalesRes.json();
        let customers = Array.isArray(data) ? data : [];
        
        // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
        if (!isAdmin || !showClosed) {
          customers = customers.filter(c => c.status !== 'closed');
        }
        
        newStats.autoSchiessAftersales = {
          total: customers.length,
          open: customers.filter(c => c.status === 'open').length,
          sold: customers.filter(c => c.status === 'sold').length,
          closed: customers.filter(c => c.status === 'closed').length
        };
      }

      if (fruhlingCheckRes.ok) {
        const data = await fruhlingCheckRes.json();
        let customers = Array.isArray(data) ? data : [];
        if (!isAdmin || !showClosed) {
          customers = customers.filter(c => c.status !== 'closed');
        }
        newStats.fruhlingCheck = {
          total: customers.length,
          open: customers.filter(c => c.status === 'open').length,
          sold: customers.filter(c => c.status === 'sold').length,
          closed: customers.filter(c => c.status === 'closed').length
        };
      }

      setWorkflowStats(newStats);
      
      await fetchWorkflowCustomers(selectedList || getInitialSelectedList());
    } catch (error) {
      console.error('Error fetching workflow data:', error);
      // La gestione degli errori 401 è ora automatica tramite authenticatedFetch
    }
  };
  
  const fetchWorkflowCustomers = async (listType) => {
    try {
      // Ottieni l'utente attuale dal localStorage
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const isAdmin = currentUser?.role === 'admin';
      
      // Mappa i tipi di lista agli endpoint
      const endpointMap = {
        jkVerkauft: 'jk/verkauft',
        jkAftersales: 'jk/aftersales',
        autoSchiessVerkauft: 'auto-schiess/verkauft',
        autoSchiessAftersales: 'auto-schiess/aftersales',
        fruhlingCheck: 'special/fruhling'
      };
      
      const endpoint = endpointMap[listType];
      if (!endpoint) {
        console.error('Invalid list type:', listType);
        return;
      }
      
      // Gli endpoint workflow non supportano show_closed, filtriamo lato frontend
      let url = `${API_BASE_URL}/workflow/${endpoint}/`;
      
      const response = await authenticatedFetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      // Aggiorna i clienti per la lista selezionata
      let customers = Array.isArray(data) ? data : [];
      
      // CONTROLLO DI SICUREZZA: Filtra i clienti in base al dipartimento per prevenire errori di caricamento
      const expectedDepartment = listType === 'fruhlingCheck'
        ? 'Special'
        : listType.startsWith('jk') ? 'JK' : 'Auto Schiess';
      customers = customers.filter(customer => {
        if (customer.current_department !== expectedDepartment) {
          console.warn(`🚨 SECURITY: Cliente ${customer.name} (ID: ${customer.id}) ha dipartimento '${customer.current_department}' ma è apparso nella lista '${listType}' (atteso: '${expectedDepartment}')`);
          return false; // Rimuovi dalla lista
        }
        return true;
      });
      
      // Filtra i clienti "closed" se l'utente non è admin o non ha attivato il toggle
      if (!isAdmin || !showClosed) {
        customers = customers.filter(customer => customer.status !== 'closed');
      }
      
      setCustomers(customers);
      
      // Aggiorna le statistiche per questa lista
      await updateListStats(listType, customers);
      
      // Reset della paginazione
      setCurrentPage(1);
      
      // Fetch call counts for all customers
      fetchCallCountsForWorkflow(customers);
    } catch (error) {
      console.error(`Error fetching customers for ${listType}:`, error);
    }
  };

  const refreshWorkflowData = async (shouldResetPage = true) => {
    try {
      await fetchWorkflowCustomers(selectedList);
      
      // Reset della paginazione solo se richiesto
      if (shouldResetPage) {
        setCurrentPage(1);
      }
    } catch (error) {
      console.error(`Error refreshing workflow data:`, error);
    }
  };
  
  // Stato per memorizzare l'ultima chiamata di ogni cliente
  const [lastCallTimes, setLastCallTimes] = useState({});

  // Funzione per ottenere i conteggi reali delle chiamate e l'ultima chiamata per i clienti workflow
  const fetchCallCountsForWorkflow = async (customers) => {
    try {
      // Ottieni il conteggio reale delle chiamate e l'ultima chiamata per ogni cliente
      const countsMap = {};
      const lastCallMap = {};
      
      // Fetch real call counts and last call time for each customer
      await Promise.all(
        customers.map(async (customer) => {
          try {
            const response = await authenticatedFetch(`${API_BASE_URL}/customers/${customer.id}/calls/`);
      
            if (response.ok) {
              const callLogs = await response.json();
              countsMap[customer.id] = callLogs.length;
              
              // Trova l'ultima chiamata (più recente)
              if (callLogs.length > 0) {
                const lastCall = callLogs.reduce((latest, current) => {
                  return new Date(current.call_time) > new Date(latest.call_time) ? current : latest;
                });
                lastCallMap[customer.id] = lastCall.call_time;
              } else {
                lastCallMap[customer.id] = null; // Mai chiamato
              }
            } else {
              // Fallback: usa jk_attempts per clienti JK, 0 per altri
              countsMap[customer.id] = customer.jk_attempts || 0;
              lastCallMap[customer.id] = null;
            }
          } catch (error) {
            console.error(`Error fetching calls for customer ${customer.id}:`, error);
            // Fallback: usa jk_attempts per clienti JK, 0 per altri  
            countsMap[customer.id] = customer.jk_attempts || 0;
            lastCallMap[customer.id] = null;
          }
        })
      );
      
      setCallCounts(countsMap);
      setLastCallTimes(lastCallMap);
    } catch (error) {
      console.error('Error setting call counts for workflow:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-orange-100 text-orange-800';
      case 'sold':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      case 'not_available':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <Flame className="h-5 w-5 text-orange-500" />;
      case 'sold':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'closed':
        return <X className="h-5 w-5 text-red-500" />;
      case 'not_available':
        return <Users className="h-5 w-5 text-yellow-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getFeedbackIcon = (feedback) => {
    switch (feedback) {
      case 'positive':
        return <ThumbsUp className="h-5 w-5 text-green-500" />;
      case 'negative':
        return <ThumbsDown className="h-5 w-5 text-red-500" />;
      case 'neutral':
        return <Minus className="h-5 w-5 text-gray-500" />;
      default:
        return null;
    }
  };

  const getFeedbackBadgeColor = (feedback) => {
    switch (feedback) {
      case 'positive':
        return 'bg-green-100 text-green-800';
      case 'negative':
        return 'bg-red-100 text-red-800';
      case 'neutral':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString, needsAdjustment = false) => {
    // Verifica se dateString è valido
    if (!dateString) {
      return 'Data non disponibile';
    }
    
    try {
      const date = new Date(dateString);
      
      // Verifica se la data è valida
      if (isNaN(date.getTime())) {
        console.error('Data non valida:', dateString);
        return 'Data non valida';
      }
      
      // Per i timestamp delle chiamate, usiamo il fuso orario europeo
      // Se needsAdjustment è true, aggiungiamo 2 ore per il fuso CET/CEST
      const adjustedDate = needsAdjustment 
        ? new Date(date.getTime() + 2 * 60 * 60 * 1000) // +2 ore in millisecondi
        : date;
      
      return new Intl.DateTimeFormat('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Berlin' // Forza il fuso orario europeo
      }).format(adjustedDate);
    } catch (error) {
      console.error('Errore nel formattare la data:', error);
      return 'Errore data';
    }
  };

  // Funzione per verificare se un determinato status è selezionabile in base al feedback
  const isStatusSelectable = (status, feedback) => {
    const effectiveFeedback = feedback || selectedFeedback;
    
    if (!effectiveFeedback) {
      return false; // Nessun feedback selezionato, non si può selezionare lo stato
    }
    
    if (effectiveFeedback === 'neutral' && status !== 'not_available') {
      return false; // Con feedback neutral, solo "nicht erreicht" può essere selezionato
    }
    
    if (effectiveFeedback === 'positive' && status === 'not_available') {
      return false; // Con feedback positive, "nicht erreicht" non può essere selezionato
    }
    
    if (effectiveFeedback === 'negative' && status !== 'closed') {
      return false; // Con feedback negative, solo "geschlossen" può essere selezionato
    }
    
    return true;
  };

  // Funzione per determinare automaticamente lo status dal feedback
  const determineStatusFromFeedback = (feedback) => {
    if (!feedback) return null;
    
    switch (feedback.toLowerCase()) {
      case 'positive':
      case 'positiv':
      case 'negative':
      case 'negativ':
        return 'closed'; // Geschlossen
      case 'neutral':
        return 'not_available'; // Nicht erreicht
      default:
        return null;
    }
  };

  // Gestisce la selezione del feedback con la nuova logica automatica
  const handleFeedbackSelect = (feedback) => {
    setPendingFeedback(feedback);
    
    // Determina automaticamente lo status in base al feedback
    const autoStatus = determineStatusFromFeedback(feedback);
    if (autoStatus) {
      setPendingStatus(autoStatus);
    }
  };

  // Gestisce la selezione dello stato con le nuove regole di business
  const handleStatusSelect = (status) => {
    // Determina il feedback da considerare (quello pendente o quello corrente)
    const effectiveFeedback = pendingFeedback || selectedFeedback;
    
    // Applica le regole di business
    if (effectiveFeedback === 'neutral' && status !== 'not_available') {
      // Con feedback neutral, solo "nicht erreicht" può essere selezionato
      return;
    }
    
    if (effectiveFeedback === 'positive' && status === 'not_available') {
      // Con feedback positive, "nicht erreicht" non può essere selezionato
      return;
    }
    
    if (effectiveFeedback === 'negative' && status !== 'closed') {
      // Con feedback negative, solo "geschlossen" può essere selezionato
      return;
    }
    
    setPendingStatus(status);
  };

  const handleUpdate = async () => {
    if (!selectedCustomer) return;
    
    // Verifica se è stato selezionato almeno un valore da aggiornare
    if (!pendingFeedback && !pendingStatus && !notes.trim()) {
      alert('Bitte wählen Sie zuerst Feedback oder Status, bevor Sie aktualisieren.');
      return;
    }
    
    // Determina automaticamente lo status dal feedback (se presente)
    const newFeedback = pendingFeedback || selectedFeedback;
    const newStatus = pendingFeedback ? determineStatusFromFeedback(pendingFeedback) : (pendingStatus || currentStatus);
    
    // Le regole di business sono ora gestite automaticamente dalla funzione determineStatusFromFeedback
    
    try {
      // Prepara i dati da inviare
      const callData = {
        customer_id: selectedCustomer.id,
        notes: notes,
        new_feedback: newFeedback,
        // new_status rimosso - ora viene determinato automaticamente dal backend
        previous_status: currentStatus,
        previous_feedback: selectedFeedback
      };
      
      // Aggiungiamo i timestamp di inizio e fine chiamata se siamo nel workflow guidato
      if (callPhase !== 'idle' && callStartTime) {
        callData.call_start_time = callStartTime.toISOString();
        
        if ((callPhase === 'documenting') && callEndTime) {
          callData.call_end_time = callEndTime.toISOString();
          
          // Calcoliamo e logghiamo la durata della chiamata
          const durationMs = callEndTime - callStartTime;
          const minutes = Math.floor(durationMs / 60000);
          const seconds = Math.floor((durationMs % 60000) / 1000);
          console.log(`Durata chiamata: ${minutes} minuti e ${seconds} secondi`);
        }
      }
      
      const response = await authenticatedFetch(`${API_BASE_URL}/calls/`, {
        method: 'POST',
        body: JSON.stringify(callData),
      });
  
      if (response.ok) {
        // Ottieni l'utente attuale dal localStorage
        const currentUser = JSON.parse(localStorage.getItem('user'));
        const isAdmin = currentUser?.role === 'admin';
        
        // Determina lo status che è stato impostato automaticamente dal backend
        const determinedStatus = newFeedback ? determineStatusFromFeedback(newFeedback) : currentStatus;
        
        // Verifica se il nuovo stato è "closed" (Geschlossen)
        if (determinedStatus === 'closed') {
          if (!isAdmin) {
            // Per gli utenti non admin, quando impostiamo lo stato a "closed",
            // dobbiamo prima aggiornare l'elenco dei clienti e poi chiudere la scheda
            
            // Aggiorniamo l'elenco dei clienti per rimuovere quelli con stato "closed"
            await refreshWorkflowData();
            
            // Ora chiudiamo la scheda del dettaglio
            setSelectedCustomer(null);
            
            // Resetta le note e i valori pendenti
            setNotes('');
            setPendingFeedback(null);
            setPendingStatus(null);
            
            // Resetta il workflow
            setCallPhase('idle');
            setCallStartTime(null);
            setCallEndTime(null);
            
            return; // Esci dalla funzione per evitare ulteriori aggiornamenti
          } else {
            // Se è admin, forza un aggiornamento completo dei dati
            console.log("Aggiornamento forzato per admin dopo impostazione stato 'closed'");
            
            // Aggiorna l'elenco dei clienti
            await refreshWorkflowData();
            
            // Aggiorna il cliente selezionato
            try {
              // Ricarica i dati della lista corrente per ottenere il cliente aggiornato
              await fetchWorkflowCustomers(selectedList);
              
              // Il cliente è già aggiornato tramite fetchWorkflowCustomers
              // Impostiamo manualmente i valori per mantenere l'UI coerente
              setCurrentStatus(determinedStatus);
              setSelectedFeedback(newFeedback);
            } catch (error) {
              console.error('Error updating selected customer:', error);
            }
            
            // Aggiorna i log delle chiamate
            await fetchCallLogs(selectedCustomer.id);
            
            // I conteggi delle chiamate sono già aggiornati tramite fetchWorkflowCustomers
            
            // Resetta le note e i valori pendenti
            setNotes('');
            setPendingFeedback(null);
            setPendingStatus(null);
          }
        }
        
        // Per tutti gli altri stati, aggiorna normalmente
        setSelectedFeedback(newFeedback);
        setCurrentStatus(determinedStatus);
        
        // Aggiorna l'elenco dei clienti senza resettare la pagina
        refreshWorkflowData(false);
        
        // Aggiorna il cliente selezionato
        updateSelectedCustomer();
        
        // Aggiorna i log delle chiamate
        fetchCallLogs(selectedCustomer.id);
        
        // I conteggi delle chiamate sono già aggiornati tramite refreshWorkflowData
        
        // Resetta le note e i valori pendenti
        setNotes('');
        setPendingFeedback(null);
        setPendingStatus(null);
      }
    } catch (error) {
      console.error('Error updating customer:', error);
    }
  };
  
  // Funzione per aggiornare il cliente selezionato quando il backend risponde
  const updateSelectedCustomer = async () => {
    if (selectedCustomer && selectedList) {
      try {
        // Ricarica i dati della lista corrente
        await fetchWorkflowCustomers(selectedList);
        
        // Il cliente sarà aggiornato automaticamente tramite fetchWorkflowCustomers
        // che aggiorna lo stato `customers`
      } catch (error) {
        console.error('Error updating selected customer:', error);
      }
    }
  };

  // Funzione per aggiornare tutti i dati del workflow
  const refreshCRMData = () => {
    console.log('Aggiornamento dati workflow...');
    fetchAllWorkflowData();
  };

  // Funzione per aggiornare le statistiche di una singola lista
  const updateListStats = async (listType, customers) => {
    // I customers qui sono già filtrati se necessario
    const stats = {
      total: customers.length,
      open: customers.filter(c => c.status === 'open').length,
      sold: customers.filter(c => c.status === 'sold').length,
      closed: customers.filter(c => c.status === 'closed').length
    };
    setWorkflowStats(prev => ({ ...prev, [listType]: stats }));
  };

  // Funzione per gestire la chiusura del pannello utente
  const handleUserPanelClose = () => {
    setShowUserPanel(false);
    // FIX: Aggiorna solo le statistiche senza resettare la lista corrente
    // Non chiamiamo refreshCRMData() che resetterebbe selectedList
    console.log('🔄 Pannello utente chiuso - mantenendo lista corrente:', selectedList);
    
    // Aggiorna solo le statistiche mantenendo la lista corrente
    if (selectedList) {
      refreshWorkflowData(false); // false = non resettare la pagina
    }
  };

  // Funzione per normalizzare i numeri di telefono (rimuove spazi e caratteri non numerici)
  const normalizePhoneNumber = (phoneNumber) => {
    return phoneNumber.replace(/\D/g, '');
  };

  // Funzione per calcolare la durata tra due timestamp
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '--:--';
    
    const durationMs = endTime - startTime;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    
    return `${minutes} Min ${seconds} Sek`;
  };

  // Nuove funzioni per il workflow guidato di chiamata
  
  // Inizia la chiamata
  const startCall = () => {
    if (!selectedCustomer) return;
    
    const now = new Date();
    setCallStartTime(now);
    setCallPhase('calling');
    
    // Utilizziamo la funzione makeCall esistente per avviare la chiamata
    makeCall(selectedCustomer.phone, selectedCustomer.id);
  };
  
  // Termina la chiamata
  const endCall = () => {
    if (!selectedCustomer || !callStartTime) return;
    
    const now = new Date();
    setCallEndTime(now);
    setCallPhase('documenting');
  };
  
  // Salva il protocollo di chiamata
  const saveCallProtocol = () => {
    // Verifica se è stato selezionato il feedback
    if (!pendingFeedback && !notes.trim()) {
      alert('Bitte wählen Sie ein Feedback aus oder geben Sie Notizen ein.');
      return;
    }
    
    // Utilizziamo la funzione handleUpdate esistente per salvare il protocollo
    handleUpdate();
    
    // Determina automaticamente lo status dal feedback per la logica del workflow
    const determinedStatus = pendingFeedback ? determineStatusFromFeedback(pendingFeedback) : currentStatus;
    
    // Ottieni l'utente attuale dal localStorage
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const isAdmin = currentUser?.role === 'admin';
    
    // Se lo stato non è "closed", resettiamo il workflow
    // Se è "closed" e non siamo admin, il reset avverrà in handleUpdate quando deseleziona il cliente
    if (determinedStatus !== 'closed' || (determinedStatus === 'closed' && isAdmin)) {
      // Non passiamo più alla fase "completed", ma resettiamo direttamente
      startNewCall();
    }
  };
  
  // Inizia una nuova chiamata
  const startNewCall = () => {
    // Reset degli stati necessari
    setCallPhase('idle');
    setPendingFeedback(null);
    setPendingStatus(null);
    setNotes('');
    setCallStartTime(null);
    setCallEndTime(null);
  };

  // Funzione per filtrare i clienti in base ai criteri di ricerca e filtro
  const filteredCustomers = useMemo(() => {
    // Se non c'è termine di ricerca, applica solo i filtri di stato e feedback
    if (!searchTerm.trim()) {
      return customers.filter(customer => {
        // Filtro per lo stato
        const statusMatch = 
          statusFilter === 'all' || 
          customer.status === statusFilter;
        
        // Filtro per il feedback
        const feedbackMatch = 
          feedbackFilter === 'all' || 
          customer.feedback === feedbackFilter;
        
        return statusMatch && feedbackMatch;
      });
    }
    
    // Normalizza il termine di ricerca per i numeri di telefono
    const normalizedSearchTerm = searchTerm.toLowerCase().trim();
    const normalizedPhoneSearchTerm = normalizePhoneNumber(searchTerm);
    
    return customers.filter(customer => {
      // Filtro per la ricerca
      const nameMatch = customer.name.toLowerCase().includes(normalizedSearchTerm);
      
      // Filtro per numero di telefono (confronta sia il numero originale che quello normalizzato)
      const phoneMatch = 
        customer.phone.toLowerCase().includes(normalizedSearchTerm) || 
        (normalizedPhoneSearchTerm.length > 0 && normalizePhoneNumber(customer.phone).includes(normalizedPhoneSearchTerm));
      
      // Filtro per numero interno
      const internMatch = customer.intern_number.toLowerCase().includes(normalizedSearchTerm);
      
      // Filtro per lo stato
      const statusMatch = 
        statusFilter === 'all' || 
        customer.status === statusFilter;
      
      // Filtro per il feedback
      const feedbackMatch = 
        feedbackFilter === 'all' || 
        customer.feedback === feedbackFilter;
      
      return (nameMatch || phoneMatch || internMatch) && statusMatch && feedbackMatch;
    });
  }, [customers, searchTerm, statusFilter, feedbackFilter]);

  // Funzione per ordinare i clienti
  const sortedCustomers = useMemo(() => {
    const sortableCustomers = [...filteredCustomers];
    
    // Se non c'è un ordinamento manuale specifico, usa l'ordinamento intelligente
    if (!sortConfig.key || sortConfig.key === 'smart') {
      return sortCustomersIntelligently(sortableCustomers);
    }
    
    // Altrimenti usa l'ordinamento manuale
    sortableCustomers.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableCustomers;
  }, [filteredCustomers, sortConfig, callCounts, lastCallTimes]);

  // Funzione per cambiare l'ordinamento
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Calcola gli indici dei clienti da visualizzare nella pagina corrente
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = sortedCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);
  
  // Calcola il numero totale di pagine
  const totalPages = Math.ceil(sortedCustomers.length / customersPerPage);
  
  // Funzione per cambiare pagina
  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    setLastPage(pageNumber);
  };
  
  // Funzione per andare alla pagina successiva
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  // Funzione per andare alla pagina precedente
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Reset della paginazione quando cambiano i filtri
  useEffect(() => {
    // Resetta la pagina solo se i filtri sono cambiati manualmente
    if (searchTerm !== '' || statusFilter !== 'all' || feedbackFilter !== 'all') {
      setCurrentPage(1);
    } else {
      // Ripristina l'ultima pagina se i filtri sono stati resettati
      setCurrentPage(lastPage);
    }
  }, [searchTerm, statusFilter, feedbackFilter, sortConfig]);
  
  // Timer per la durata della chiamata
  useEffect(() => {
    let timer;
    if (callPhase === 'calling' && callStartTime) {
      timer = setInterval(() => {
        const durationElement = document.getElementById('call-duration');
        if (durationElement) {
          const now = new Date();
          const durationMs = now - callStartTime;
          const minutes = Math.floor(durationMs / 60000);
          const seconds = Math.floor((durationMs % 60000) / 1000);
          durationElement.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        }
      }, 1000);
    }
    
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [callPhase, callStartTime]);

  // Funzione per verificare se c'è un protocollo di chiamata in corso
  const isCallProtocolInProgress = () => {
    return callPhase === 'calling' || callPhase === 'documenting';
  };

  // Funzione per gestire il cambio di cliente con controllo
  const handleCustomerChange = (customer) => {
    if (isCallProtocolInProgress()) {
      setPendingAction(() => () => {
        setSelectedCustomer(customer);
        if (isMobileView) {
          setActiveView('details');
        }
      });
      setShowConfirmDialog(true);
    } else {
      setSelectedCustomer(customer);
      if (isMobileView) {
        setActiveView('details');
      }
    }
  };

  // Funzione per validare l'accesso a una lista basata sul dipartimento
  const canAccessList = (listType) => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    const userDepartment = currentUser?.department;
    const isAdmin = currentUser?.role === 'admin';
    
    // Gli admin possono accedere a tutto
    if (isAdmin) return true;
    
    // Gli utenti JK possono accedere solo alle liste JK
    if (userDepartment === 'JK') {
      return listType === 'jkVerkauft' || listType === 'jkAftersales';
    }
    
    // Gli utenti Auto Schiess possono accedere solo alle liste AHS (gestisce entrambe le varianti)
    if (userDepartment === 'Auto Schiess' || userDepartment === 'AutoSchiess') {
      return listType === 'autoSchiessVerkauft' || listType === 'autoSchiessAftersales';
    }

    if (userDepartment === 'Special') {
      return listType === 'fruhlingCheck';
    }
    
    // Se il dipartimento è null, undefined o stringa vuota, nega l'accesso (eccetto admin)
    if (!userDepartment || userDepartment === '' || userDepartment === null) {
      return false; // Utenti senza dipartimento non possono accedere a nessuna lista
    }
    
    // Fallback: nega l'accesso per dipartimenti non riconosciuti
    return false;
  };

  // Funzione per gestire il cambio di lista workflow con controllo
  const handleListChange = (listType) => {
    // Verifica se l'utente può accedere a questa lista
    if (!canAccessList(listType)) {
      console.warn(`Accesso negato alla lista ${listType} per l'utente del dipartimento ${JSON.parse(localStorage.getItem('user'))?.department}`);
      return;
    }
    
    if (isCallProtocolInProgress()) {
      setPendingAction(() => () => {
        setSelectedList(listType);
        setSelectedCustomer(null);
        if (isMobileView) {
          setActiveView('customers');
        }
      });
      setShowConfirmDialog(true);
    } else {
      setSelectedList(listType);
      setSelectedCustomer(null);
      if (isMobileView) {
        setActiveView('customers');
      }
    }
  };

  // Funzione per eseguire l'azione in sospeso
  const executePendingAction = () => {
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
    setShowConfirmDialog(false);
    
    // Reset del workflow
    setCallPhase('idle');
    setCallStartTime(null);
    setCallEndTime(null);
    setNotes('');
    setPendingFeedback(null);
    setPendingStatus(null);
  };

  // Funzione per annullare l'azione in sospeso
  const cancelPendingAction = () => {
    setPendingAction(null);
    setShowConfirmDialog(false);
  };

  // Effetto per gestire l'avviso quando l'utente tenta di ricaricare o abbandonare la pagina
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isCallProtocolInProgress()) {
        // Messaggio standard per la maggior parte dei browser
        const message = "Sie haben einen aktiven Anrufprotokoll, der noch nicht gespeichert wurde. Wenn Sie die Seite verlassen, gehen alle eingegebenen Daten verloren.";
        e.returnValue = message; // Standard per Chrome
        return message; // Per altri browser
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [callPhase]);

  // Funzione per tornare alla vista precedente
  const goBack = () => {
    // Se siamo nella vista dettaglio cliente e c'è un protocollo di chiamata in corso
    if (activeView === 'details' && isCallProtocolInProgress()) {
      // Imposta l'azione da eseguire dopo la conferma
      setPendingAction(() => () => {
        setActiveView('customers');
      });
      // Mostra il dialogo di conferma
      setShowConfirmDialog(true);
    } else {
      // Comportamento normale se non c'è un protocollo in corso
      if (activeView === 'details') {
        setActiveView('customers');
      } else if (activeView === 'customers') {
        setActiveView('campaigns');
      }
    }
  };

  // Funzione per effettuare una chiamata telefonica
  const callCustomer = () => {
    if (!selectedCustomer) return;
    
    // Utilizziamo la funzione makeCall esistente
    makeCall(selectedCustomer.phone, selectedCustomer.id);
    
    // Avviamo il workflow di chiamata
    startCall();
  };

  // Funzione per verificare se un cliente ha un feedback specifico
  const hasCustomerFeedback = (customer, feedback) => {
    return customer.feedback === feedback;
  };

  // Funzione per ottenere il conteggio dei clienti per feedback
  const getCustomerCountByFeedback = (feedback) => {
    return customers.filter(c => c.feedback === feedback).length;
  };

  // Funzione per verificare se ci sono modifiche in sospeso
  const hasPendingChanges = () => {
    return pendingFeedback !== null || pendingStatus !== null || notes.trim() !== '';
  };

  // Funzione per ottenere il nome tradotto della lista
  const getListDisplayName = (listType) => {
    const names = {
      jkVerkauft: 'JK Verkauft',
      jkAftersales: 'JK Aftersales', 
      autoSchiessVerkauft: 'AHS Verkauft',
      autoSchiessAftersales: 'AHS Aftersales',
      fruhlingCheck: 'Kostenlose Fruhling Check'
    };
    return names[listType] || listType;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-indigo-600 to-indigo-800 shadow-md">
        <div className="max-w-7xl mx-auto py-4 px-4 flex justify-between items-center">
          <div className="flex items-center">
            {/* Logo Pulito e Professionale */}
            <div className="flex items-center">
              <div>
                <div className="flex items-center">
                  <span className="text-2xl font-bold text-white tracking-wide">BONOBO</span>
                  <span className="ml-3 text-sm text-white text-opacity-75 font-medium">CRM</span>
            </div>
                <div className="text-xs text-white text-opacity-60 -mt-0.5">Customer Relations Management</div>
          </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
          <UserProfile onShowUserPanel={setShowUserPanel} />
          </div>
        </div>
      </header>

      {showUserPanel && (
        <div className="fixed inset-0 z-50 overflow-auto bg-gray-600 bg-opacity-75 flex">
          <div className="relative p-6 bg-white w-full max-w-7xl h-[90vh] m-auto rounded-lg shadow-xl overflow-y-auto">
            <UserPanel onClose={handleUserPanelClose} />
          </div>
        </div>
      )}

      {/* Dialogo di conferma per l'abbandono del protocollo di chiamata */}
      {showConfirmDialog && (
        <div className="fixed inset-0 z-50 overflow-auto bg-gray-600 bg-opacity-75 flex items-center justify-center transition-opacity duration-300 ease-in-out">
          <div 
            className="relative mx-4 w-full max-w-md bg-white rounded-lg shadow-xl transform transition-all duration-300 ease-in-out"
            style={{ maxHeight: '90vh' }}
          >
            <div className="p-4 sm:p-6 overflow-y-auto">
              <div className="text-center">
                <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 text-red-500 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Achtung! Ungespeicherte Änderungen</h3>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
                  Sie haben einen aktiven Anrufprotokoll, der noch nicht gespeichert wurde. 
                  Wenn Sie fortfahren, gehen alle eingegebenen Daten verloren.
                </p>
                <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4">
                  <button
                    onClick={cancelPendingAction}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 text-sm font-medium transition-colors duration-200"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={executePendingAction}
                    className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium transition-colors duration-200"
                  >
                    Fortfahren
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Layout responsive con animazioni di slide per workflow */}
        <div className={`flex ${isMobileView ? 'flex-col' : 'flex-row gap-6'}`}>
          {/* Pannello Liste Workflow */}
          <div 
            className={`
              bg-white rounded-lg shadow-sm overflow-y-auto transition-all duration-300 ease-in-out
              ${isMobileView 
                ? `${activeView === 'campaigns' ? 'block' : 'hidden'} w-full h-[calc(100vh-8rem)]` 
                : 'w-80 flex-shrink-0 max-h-[calc(100vh-2rem)]'
              }
            `}
            style={{ minWidth: isMobileView ? 'auto' : '320px' }}
          >
            {/* Header con gradiente */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 p-4 text-white rounded-lg">
              <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-semibold">Kampagnen</h2>
              {isMobileView && activeView !== 'campaigns' && (
                <button
                  onClick={() => setActiveView('campaigns')}
                    className="p-2 rounded-full hover:bg-white hover:bg-opacity-10"
                >
                    <Menu className="h-5 w-5 text-white" />
                </button>
              )}
            </div>
            </div>
            
            {/* Liste Workflow */}
            <div className="p-4 space-y-3">
              {(() => {
                // Ottieni l'utente corrente e il suo dipartimento
                const currentUser = JSON.parse(localStorage.getItem('user'));
                const userDepartment = currentUser?.department;
                const isAdmin = currentUser?.role === 'admin';
                
                
                // Componente per le liste JK
                const JKWorkflowLists = () => (
                  <>
                    {/* JK Verkauft */}
                    <div
                      className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all border-2
                        ${selectedList === 'jkVerkauft' 
                          ? 'border-indigo-500 shadow-md bg-indigo-50' 
                          : 'border-gray-200 hover:shadow-md hover:border-indigo-300'}`}
                      onClick={() => handleListChange('jkVerkauft')}
                    >
                      <div className="flex items-center">
                        <h3 className="font-medium text-gray-900">JK Verkauft</h3>
                      </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="h-4 w-4 mr-1" />
                          <span>{workflowStats.jkVerkauft?.total || 0} Kunden</span>
                      </div>
                        <div className="flex items-center text-sm text-orange-600">
                          <Flame className="h-4 w-4 mr-1" />
                          <span>{workflowStats.jkVerkauft?.open || 0} Neu</span>
                      </div>
                        {JSON.parse(localStorage.getItem('user'))?.role === 'admin' && showClosed && (
                          <div className="flex items-center text-sm text-red-600">
                            <X className="h-4 w-4 mr-1" />
                            <span>{workflowStats.jkVerkauft?.closed || 0} Geschlossen</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* JK Aftersales */}
                    <div
                      className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all border-2
                        ${selectedList === 'jkAftersales' 
                          ? 'border-indigo-500 shadow-md bg-indigo-50' 
                          : 'border-gray-200 hover:shadow-md hover:border-indigo-300'}`}
                      onClick={() => handleListChange('jkAftersales')}
                    >
                      <div className="flex items-center">
                        <h3 className="font-medium text-gray-900">JK Aftersales</h3>
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{workflowStats.jkAftersales?.total || 0} Kunden</span>
                        </div>
                        <div className="flex items-center text-sm text-orange-600">
                          <Flame className="h-4 w-4 mr-1" />
                          <span>{workflowStats.jkAftersales?.open || 0} Neu</span>
                      </div>
                      {JSON.parse(localStorage.getItem('user'))?.role === 'admin' && showClosed && (
                        <div className="flex items-center text-sm text-red-600">
                          <X className="h-4 w-4 mr-1" />
                            <span>{workflowStats.jkAftersales?.closed || 0} Geschlossen</span>
                        </div>
                      )}
                      </div>
                    </div>
                  </>
                );
                
                // Componente per le liste AHS
                const AHSWorkflowLists = () => (
                  <>
                    {/* AHS Verkauft */}
                    <div
                      className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all border-2
                        ${selectedList === 'autoSchiessVerkauft' 
                          ? 'border-indigo-500 shadow-md bg-indigo-50' 
                          : 'border-gray-200 hover:shadow-md hover:border-indigo-300'}`}
                      onClick={() => handleListChange('autoSchiessVerkauft')}
                    >
                      <div className="flex items-center">
                        <h3 className="font-medium text-gray-900">AHS Verkauft</h3>
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{workflowStats.autoSchiessVerkauft?.total || 0} Kunden</span>
                        </div>
                        <div className="flex items-center text-sm text-orange-600">
                          <Flame className="h-4 w-4 mr-1" />
                          <span>{workflowStats.autoSchiessVerkauft?.open || 0} Neu</span>
                        </div>
                        {JSON.parse(localStorage.getItem('user'))?.role === 'admin' && showClosed && (
                          <div className="flex items-center text-sm text-red-600">
                            <X className="h-4 w-4 mr-1" />
                            <span>{workflowStats.autoSchiessVerkauft?.closed || 0} Geschlossen</span>
                    </div>
                  )}
                </div>
            </div>
                    
                    {/* AHS Aftersales */}
                    <div
                      className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all border-2
                        ${selectedList === 'autoSchiessAftersales' 
                          ? 'border-indigo-500 shadow-md bg-indigo-50' 
                          : 'border-gray-200 hover:shadow-md hover:border-indigo-300'}`}
                      onClick={() => handleListChange('autoSchiessAftersales')}
                    >
                      <div className="flex items-center">
                        <h3 className="font-medium text-gray-900">AHS Aftersales</h3>
                      </div>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm text-gray-500">
                          <Users className="h-4 w-4 mr-1" />
                          <span>{workflowStats.autoSchiessAftersales?.total || 0} Kunden</span>
                        </div>
                        <div className="flex items-center text-sm text-orange-600">
                          <Flame className="h-4 w-4 mr-1" />
                          <span>{workflowStats.autoSchiessAftersales?.open || 0} Neu</span>
                        </div>
                        {JSON.parse(localStorage.getItem('user'))?.role === 'admin' && showClosed && (
                          <div className="flex items-center text-sm text-red-600">
                            <X className="h-4 w-4 mr-1" />
                            <span>{workflowStats.autoSchiessAftersales?.closed || 0} Geschlossen</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
                
                const FruhlingWorkflowList = () => (
                  <div
                    className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all border-2
                      ${selectedList === 'fruhlingCheck'
                        ? 'border-indigo-500 shadow-md bg-indigo-50'
                        : 'border-gray-200 hover:shadow-md hover:border-indigo-300'}`}
                    onClick={() => handleListChange('fruhlingCheck')}
                  >
                    <div className="flex items-center">
                      <h3 className="font-medium text-gray-900">Kostenlose Fruhling Check</h3>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center text-sm text-gray-500">
                        <Users className="h-4 w-4 mr-1" />
                        <span>{workflowStats.fruhlingCheck?.total || 0} Kunden</span>
                      </div>
                      <div className="flex items-center text-sm text-orange-600">
                        <Flame className="h-4 w-4 mr-1" />
                        <span>{workflowStats.fruhlingCheck?.open || 0} Neu</span>
                      </div>
                      {JSON.parse(localStorage.getItem('user'))?.role === 'admin' && showClosed && (
                        <div className="flex items-center text-sm text-red-600">
                          <X className="h-4 w-4 mr-1" />
                          <span>{workflowStats.fruhlingCheck?.closed || 0} Geschlossen</span>
                        </div>
                      )}
                    </div>
                  </div>
                );

                // Logica di visualizzazione basata sul dipartimento
                if (isAdmin) {
                  // Gli admin vedono tutte le liste
                  console.log('✅ Mostrando TUTTE le liste per ADMIN');
                  return (
                    <>
                      <JKWorkflowLists />
                      <AHSWorkflowLists />
                      <FruhlingWorkflowList />
                    </>
                  );
                } else if (userDepartment === 'JK') {
                  // Gli utenti JK vedono solo le liste JK
                  console.log('✅ Mostrando solo liste JK per utente JK');
                  return <JKWorkflowLists />;
                } else if (userDepartment === 'Auto Schiess' || userDepartment === 'AutoSchiess') {
                  // Gli utenti Auto Schiess vedono solo le liste AHS (gestisce entrambe le varianti)
                  console.log('✅ Mostrando solo liste AHS per utente Auto Schiess');
                  return <AHSWorkflowLists />;
                } else if (userDepartment === 'Special') {
                  return <FruhlingWorkflowList />;
                } else if (!userDepartment || userDepartment === '' || userDepartment === null) {
                  // Utenti senza dipartimento valido non vedono nessuna lista
                  console.log('❌ Utente senza dipartimento valido - nessuna lista disponibile');
                  return (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <p className="text-yellow-800 font-medium">Zugang Eingeschränkt</p>
                      <p className="text-yellow-600 text-sm mt-1">
                        Ihrem Konto ist keine Abteilung zugeordnet. Bitte kontaktieren Sie den Administrator.
                      </p>
                    </div>
                  );
                } else {
                  // Abteilung nicht erkannt
                  console.log('❌ Abteilung nicht erkannt:', userDepartment);
                  return (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                      <p className="text-red-800 font-medium">Abteilung Nicht Erkannt</p>
                      <p className="text-red-600 text-sm mt-1">
                        Abteilung "{userDepartment}" ist ungültig. Bitte kontaktieren Sie den Administrator.
                      </p>
                    </div>
                  );
                }
              })()}
            </div>
            
          </div>

          {/* Pannello Clienti */}
          {!selectedList ? (
            <div className="w-[380px] flex-shrink-0 bg-white rounded-lg shadow-sm flex items-center justify-center">
              <div className="text-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Wird geladen...</p>
              </div>
            </div>
          ) : (
            <div 
              className={`
                bg-white rounded-lg shadow-sm overflow-y-auto transition-all duration-300 ease-in-out
                ${isMobileView 
                  ? `${activeView === 'customers' ? 'block' : 'hidden'} w-full h-[calc(100vh-8rem)]` 
                  : 'w-[380px] flex-shrink-0 max-h-[calc(100vh-2rem)]'
                }
              `}
              style={{ minWidth: isMobileView ? 'auto' : '380px' }}
            >
              {/* Header con gradiente */}
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 p-4 text-white rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-lg font-semibold">{getListDisplayName(selectedList)}</h2>
                  <div className="flex items-center space-x-2">
                    {/* Toggle per clienti chiusi (solo admin) */}
                    {JSON.parse(localStorage.getItem('user'))?.role === 'admin' && (
                  <button
                        onClick={() => setShowClosed(!showClosed)}
                        className={`p-2 rounded-full transition-colors ${
                          showClosed 
                            ? 'bg-white bg-opacity-20 text-white' 
                            : 'hover:bg-white hover:bg-opacity-10 text-white text-opacity-70'
                        }`}
                        title="Geschlossene Kunden anzeigen"
                      >
                        <EyeOff className="h-4 w-4" />
                  </button>
                )}
                    {/* Icona filtri */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                      className="p-2 rounded-full hover:bg-white hover:bg-opacity-10 relative"
                      title="Filter und Sortierung"
                    >
                      <Filter className="h-4 w-4 text-white" />
                      {(statusFilter !== 'all' || feedbackFilter !== 'all' || sortConfig.key !== 'smart' || sortConfig.direction !== 'ascending') && (
                        <span className="absolute top-0 right-0 h-2 w-2 bg-yellow-400 rounded-full"></span>
                    )}
                  </button>
                    {isMobileView && activeView !== 'customers' && (
                      <button
                        onClick={() => setActiveView('customers')}
                        className="p-2 rounded-full hover:bg-white hover:bg-opacity-10"
                      >
                        <Menu className="h-5 w-5 text-white" />
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Barra di ricerca nell'header */}
                <div className="relative">
                        <input
                    type="text"
                    placeholder="Suche nach Name, Telefon..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 bg-white bg-opacity-20 border border-white border-opacity-30 rounded-md text-white placeholder-white placeholder-opacity-70 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 focus:bg-opacity-30"
                  />
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white text-opacity-70" />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')}
                      className="absolute right-8 top-1/2 transform -translate-y-1/2 text-white text-opacity-70 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="p-4">

              {/* Pannello dei filtri (collassabile) */}
              {showFilters && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-medium text-gray-700 flex items-center">
                      <Filter className="h-4 w-4 mr-1" />
                      Filter und Sortierung
                    </h3>
                    <button 
                      onClick={() => setShowFilters(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Filtri */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="all">Alle Status</option>
                        <option value="open">Neu</option>
                        {/* <option value="sold">Verkauft</option> - Nascosto come richiesto */}
                        <option value="not_available">Nicht erreicht</option>
                        {JSON.parse(localStorage.getItem('user'))?.role === 'admin' && <option value="closed">Geschlossen</option>}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Feedback</label>
                      <select
                        value={feedbackFilter}
                        onChange={(e) => setFeedbackFilter(e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="all">Alle Feedback</option>
                        <option value="positive">Positiv</option>
                        <option value="neutral">Neutral</option>
                        <option value="negative">Negativ</option>
                      </select>
                    </div>
                    
                    {/* Ordinamento */}
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Sortierung</label>
                      <div className="flex items-center space-x-2">
                        <select
                          value={sortConfig.key}
                          onChange={(e) => setSortConfig({ ...sortConfig, key: e.target.value })}
                          className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="smart">Intelligente Sortierung</option>
                          <option value="name">Name</option>
                          <option value="contract_date">Vertragsdatum</option>
                        </select>
                        
                        <button
                          onClick={() => setSortConfig({ 
                            ...sortConfig, 
                            direction: sortConfig.direction === 'ascending' ? 'descending' : 'ascending' 
                          })}
                          className="p-1.5 border border-gray-300 rounded-md hover:bg-gray-50"
                          title={sortConfig.direction === 'ascending' ? 'Aufsteigend' : 'Absteigend'}
                        >
                          {sortConfig.direction === 'ascending' ? 
                            <SortAsc className="h-4 w-4 text-gray-600" /> : 
                            <SortDesc className="h-4 w-4 text-gray-600" />
                          }
                        </button>
                      </div>
                    </div>
                    
                    {/* Pulsanti di azione */}
                    <div className="sm:col-span-2 flex justify-end mt-2">
                      <button
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          setFeedbackFilter('all');
                          setSortConfig({ key: 'smart', direction: 'ascending' });
                        }}
                        className="px-3 py-1.5 text-xs text-gray-700 hover:text-gray-900 hover:underline"
                      >
                        Zurücksetzen
                      </button>
                      <button
                        onClick={() => setShowFilters(false)}
                        className="ml-2 px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700"
                      >
                        Anwenden
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Risultati della ricerca */}
              <div className="text-xs text-gray-500 mb-2 flex justify-between items-center">
                <span>{filteredCustomers.length} Kunden gefunden</span>
                {(searchTerm || statusFilter !== 'all' || feedbackFilter !== 'all') && (
                  <div className="flex items-center space-x-1">
                    {searchTerm && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                        Suche: {searchTerm}
                        <button onClick={() => setSearchTerm('')} className="ml-1 text-indigo-600 hover:text-indigo-800">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {statusFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Status: {translateStatus(statusFilter)}
                        <button onClick={() => setStatusFilter('all')} className="ml-1 text-blue-600 hover:text-blue-800">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {feedbackFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Feedback: {feedbackFilter === 'positive' ? 'Positiv' : feedbackFilter === 'negative' ? 'Negativ' : 'Neutral'}
                        <button onClick={() => setFeedbackFilter('all')} className="ml-1 text-green-600 hover:text-green-800">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                {currentCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className={`bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all
                      ${selectedCustomer?.id === customer.id 
                        ? 'ring-2 ring-indigo-500 shadow-md' 
                        : 'hover:shadow-md border border-gray-200'}`}
                    onClick={() => handleCustomerChange(customer)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900">{customer.name}</h3>
                        <div className="ml-2">
                          {getFeedbackIcon(customer.feedback)}
                        </div>
                      </div>
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                        {getStatusIcon(customer.status)}
                        <span>{translateStatus(customer.status)}</span>
                      </span>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">{customer.phone}</p>
                      </div>
                      <p className="text-xs text-gray-500">Vertragsdatum: {formatContractDate(customer.contract_date)}</p>
                      
                      {/* Aggiungiamo qui il contatore delle chiamate */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                        <p className="text-xs text-gray-500">Anrufe:</p>
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          <Phone className="h-3 w-3 text-indigo-600" />
                          <span>{callCounts[customer.id] || 0}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Paginazione */}
              {sortedCustomers.length > customersPerPage && (
                <div className="mt-4 flex flex-col space-y-2">
                  <div className="text-xs text-gray-500 text-center">
                    Zeige <span className="font-medium">{indexOfFirstCustomer + 1}</span> bis <span className="font-medium">{Math.min(indexOfLastCustomer, sortedCustomers.length)}</span> von <span className="font-medium">{sortedCustomers.length}</span> Kunden
                  </div>
                  <div className="flex justify-center items-center">
                    <button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      className={`px-2 py-1 rounded-md text-sm font-medium ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                      }`}
                    >
                      &laquo;
                    </button>
                    
                    {/* Logica per mostrare un numero limitato di pagine */}
                    {(() => {
                      // Mostra massimo 5 pagine alla volta
                      const maxPagesToShow = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                      
                      // Aggiusta startPage se siamo vicini alla fine
                      if (endPage - startPage + 1 < maxPagesToShow) {
                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                      }
                      
                      const pages = [];
                      
                      // Aggiungi prima pagina e ellipsis se necessario
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => paginate(1)}
                            className="px-2 py-1 rounded-md text-sm font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(
                            <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                              ...
                            </span>
                          );
                        }
                      }
                      
                      // Aggiungi pagine centrali
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => paginate(i)}
                            className={`px-2 py-1 rounded-md text-sm font-medium ${
                              currentPage === i
                                ? 'bg-indigo-600 text-white'
                                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      // Aggiungi ultima pagina e ellipsis se necessario
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(
                            <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => paginate(totalPages)}
                            className="px-2 py-1 rounded-md text-sm font-medium bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                          >
                            {totalPages}
                          </button>
                        );
                      }
                      
                      return pages;
                    })()}
                    
                    <button
                      onClick={nextPage}
                      disabled={currentPage === totalPages}
                      className={`px-2 py-1 rounded-md text-sm font-medium ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                      }`}
                    >
                      &raquo;
                    </button>
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {/* Pannello Dettagli Cliente */}
          {selectedCustomer && (
            <div 
              className={`
                bg-white shadow-sm rounded-lg overflow-y-auto transition-all duration-300 ease-in-out
                ${isMobileView 
                  ? `${activeView === 'details' ? 'block' : 'hidden'} w-full h-[calc(100vh-8rem)]` 
                  : 'flex-grow max-h-[calc(100vh-2rem)]'
                }
              `}
            >
              {/* Header con gradiente */}
              <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 p-4 text-white rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h2 className="text-lg font-semibold">Kundendetails</h2>
                  {isMobileView && activeView !== 'details' && (
                  <button
                      onClick={() => setActiveView('details')}
                      className="p-2 rounded-full hover:bg-white hover:bg-opacity-10"
                  >
                      <Menu className="h-5 w-5 text-white" />
                  </button>
              )}
                </div>
              </div>
              
              <div className="p-4">
              <div className="border-b pb-4 mb-4">
                <div className="mt-4 space-y-2">
                  <p className="text-gray-600">Name: <span className="font-medium text-gray-900">{selectedCustomer.name}</span></p>
                  <div className="flex items-center">
                    <p className="text-gray-600">Telefon: <span className="font-medium text-gray-900">{selectedCustomer.phone}</span></p>
                  </div>
                  <p className="text-gray-600">Vertragsdatum: <span className="font-medium text-gray-900">{formatContractDate(selectedCustomer.contract_date)}</span></p>
                  <div className="flex items-center">
                    <p className="text-gray-600 mr-2">Status:</p>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedCustomer.status)}`}>
                      {getStatusIcon(selectedCustomer.status)}
                      {translateStatus(selectedCustomer.status)}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <p className="text-gray-600 mr-2">Feedback:</p>
                    <span className="inline-flex items-center">
                      {getFeedbackIcon(selectedCustomer.feedback)}
                      <span className="ml-1 text-sm">
                        {selectedCustomer.feedback === 'positive' ? 'Positiv' : 
                         selectedCustomer.feedback === 'negative' ? 'Negativ' : 'Neutral'}
                      </span>
                    </span>
                  </div>
                </div>
                {selectedCustomer.current_department === 'Special' && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={markOfferInterest}
                      disabled={Boolean(offerInterest) || offerInterestSaving}
                      className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                        offerInterest || offerInterestSaving
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      <Check className="mr-2 h-5 w-5" />
                      {offerInterest ? 'Bereits gemeldet' : 'Am Angebot interessiert'}
                    </button>
                  </div>
                )}
              </div>

              {/* Contenuto condizionale in base alla fase del workflow */}
              {callPhase === 'idle' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium mb-4">Anruf Protokoll</h3>
                  <div className="p-4 bg-indigo-50 rounded-lg mb-4">
                    <p className="text-indigo-700 font-medium mb-2">Neuen Anruf starten</p>
                    <p className="text-sm text-indigo-600 mb-4">Klicken Sie auf die Schaltfläche unten, um einen neuen Anruf zu starten.</p>
                    <button
                      onClick={startCall}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Phone className="mr-2 h-5 w-5" />
                      Anruf starten
                    </button>
                  </div>
                </div>
              )}
              
              {callPhase === 'calling' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium mb-4">Anruf läuft</h3>
                  <div className="p-4 bg-blue-50 rounded-lg mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-blue-700 font-medium">Aktiver Anruf mit {selectedCustomer.name}</p>
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        <span id="call-duration">00:00</span>
                      </span>
                    </div>
                    <p className="text-sm text-blue-600 mb-2">Telefon: {selectedCustomer.phone}</p>
                    <p className="text-sm text-blue-600 mb-4">Beginn: {callStartTime ? callStartTime.toLocaleTimeString() : '--:--'}</p>
                    <button
                      onClick={endCall}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                      <X className="mr-2 h-5 w-5" />
                      Anruf beenden
                    </button>
                  </div>
                </div>
              )}
              
              {callPhase === 'documenting' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium mb-4">Anruf Protokoll</h3>
                  
                  <div className="p-4 bg-green-50 rounded-lg mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <p className="text-green-700 font-medium">Anruf beendet</p>
                      <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        {callStartTime && callEndTime ? 
                          `${Math.floor((callEndTime - callStartTime) / 60000)} Min ${Math.floor(((callEndTime - callStartTime) % 60000) / 1000)} Sek` : 
                          '--:--'}
                      </span>
                    </div>
                    <p className="text-sm text-green-600 mb-2">Beginn: {callStartTime ? callStartTime.toLocaleTimeString() : '--:--'}</p>
                    <p className="text-sm text-green-600 mb-4">Ende: {callEndTime ? callEndTime.toLocaleTimeString() : '--:--'}</p>
                    <p className="text-sm text-green-700 font-medium">Bitte dokumentieren Sie den Anruf:</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notizen <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      rows="3"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Feedback wählen <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 mb-4">
                      <button
                        className={`flex-1 inline-flex justify-center items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-white ${
                          pendingFeedback === 'positive' 
                            ? 'bg-green-600 ring-2 ring-green-500 ring-offset-2' 
                            : pendingFeedback ? 'bg-green-600 hover:bg-green-700' : 
                              selectedCustomer.feedback === 'positive' ? 'bg-green-500' : 'bg-green-600 hover:bg-green-700'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
                        onClick={() => handleFeedbackSelect('positive')}
                      >
                        <UserCheck className="mr-2 h-4 w-4" />
                        Positiv
                      </button>
                      <button
                        className={`flex-1 inline-flex justify-center items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-white ${
                          pendingFeedback === 'neutral' 
                            ? 'bg-yellow-600 ring-2 ring-yellow-500 ring-offset-2' 
                            : pendingFeedback ? 'bg-yellow-600 hover:bg-yellow-700' : 
                              selectedCustomer.feedback === 'neutral' ? 'bg-yellow-500' : 'bg-yellow-600 hover:bg-yellow-700'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500`}
                        onClick={() => handleFeedbackSelect('neutral')}
                      >
                        <Phone className="mr-2 h-4 w-4" />
                        Nicht erreicht
                      </button>
                      <button
                        className={`flex-1 inline-flex justify-center items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-white ${
                          pendingFeedback === 'negative' 
                            ? 'bg-red-600 ring-2 ring-red-500 ring-offset-2' 
                            : pendingFeedback ? 'bg-red-600 hover:bg-red-700' : 
                              selectedCustomer.feedback === 'negative' ? 'bg-red-500' : 'bg-red-600 hover:bg-red-700'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                        onClick={() => handleFeedbackSelect('negative')}
                      >
                        <AlertCircle className="mr-2 h-4 w-4" />
                        Negativ
                      </button>
                    </div>
                    
                  </div>

                  <div className="mt-6">
                    <button
                      className={`w-full inline-flex justify-center items-center px-4 py-3 border border-transparent rounded-md shadow-sm text-md font-medium text-white
                        ${(pendingFeedback || notes.trim()) ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed'}
                      `}
                      onClick={saveCallProtocol}
                      disabled={!pendingFeedback && !notes.trim()}
                    >
                      <Check className="mr-2 h-5 w-5" />
                      Protokoll speichern
                    </button>
                  </div>
                </div>
              )}

              {/* Anrufverlauf */}
              {callLogs.length > 0 && (
                <div className="mt-6 border-t pt-4">
                  <h3 className="text-lg font-medium mb-3">Anrufverlauf</h3>
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {callLogs.map((log) => (
                      <div key={log.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                        {/* Intestazione del log con utente e data */}
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex flex-col">
                          <div className="flex items-center mb-2">
                            <span className="text-xs text-gray-500">Bearbeitet von:</span>
                            <span className="ml-1 text-xs font-medium text-gray-700">{log.username}</span>
                          </div>
                          
                          {/* Registrazione chiamata */}
                          <div className="w-full">
                            <CallRecording callId={log.id} isAvailable={log.recording_available || false} />
                          </div>
                        </div>
                        
                        {/* Dettagli della chiamata */}
                        {log.call_start_time && (
                          <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
                            <div className="flex items-center">
                              <div className="font-medium text-indigo-700 text-sm">Anruf Details</div>
                            </div>
                            {log.call_start_time && log.call_end_time && (
                              <div className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                                <Clock className="h-3 w-3 inline mr-1" />
                                Dauer: {calculateDuration(new Date(log.call_start_time), new Date(log.call_end_time))}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Corpo del log */}
                        <div className="px-4 py-3">
                          {/* Timestamp di inizio e fine chiamata */}
                          {log.call_start_time && (
                            <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                              <div className="flex items-center">
                                <span className="text-gray-500 mr-1">Beginn:</span>
                                <span className="font-medium text-gray-700">
                                  {formatDate(log.call_start_time, true)}
                                </span>
                              </div>
                              {log.call_end_time && (
                                <div className="flex items-center">
                                  <span className="text-gray-500 mr-1">Ende:</span>
                                  <span className="font-medium text-gray-700">
                                    {formatDate(log.call_end_time, true)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Cambiamenti di feedback e status */}
                          <div className="space-y-2 mb-3">
                            {log.previous_feedback !== log.new_feedback && (
                              <div className="flex items-center">
                                <span className="text-xs text-gray-500 w-20">Feedback:</span>
                                <div className="flex items-center">
                                  {log.previous_feedback && (
                                    <>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getFeedbackBadgeColor(log.previous_feedback)}`}>
                                        {log.previous_feedback === 'positive' ? 'Positiv' : 
                                         log.previous_feedback === 'negative' ? 'Negativ' : 'Neutral'}
                                      </span>
                                      <ArrowRight className="h-3 w-3 mx-1 text-gray-400" />
                                    </>
                                  )}
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getFeedbackBadgeColor(log.new_feedback)}`}>
                                    {log.new_feedback === 'positive' ? 'Positiv' : 
                                     log.new_feedback === 'negative' ? 'Negativ' : 'Neutral'}
                                  </span>
                                </div>
                              </div>
                            )}
                            
                            {log.previous_status !== log.new_status && (
                              <div className="flex items-center">
                                <span className="text-xs text-gray-500 w-20">Status:</span>
                                <div className="flex items-center">
                                  {log.previous_status && (
                                    <>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(log.previous_status)}`}>
                                        {translateStatus(log.previous_status)}
                                      </span>
                                      <ArrowRight className="h-3 w-3 mx-1 text-gray-400" />
                                    </>
                                  )}
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(log.new_status)}`}>
                                    {translateStatus(log.new_status)}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Note della chiamata */}
                          {log.notes && (
                            <div className="mt-2">
                              <div className="text-xs text-gray-500 mb-1">Notizen:</div>
                              <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{log.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CRM;